/**
 * Certificate Recognition Carousel
 *
 * Vanilla JS, no dependencies. Ported from the UGC Testimonial Slider pattern
 * (assets/ugc-testimonial-slider.js) — same autoplay/dots/arrows/pause-on-hover
 * mechanics, scoped per-instance to `this.root` so multiple copies of this
 * section can coexist on one page. Two additions beyond that base pattern:
 * touch swipe navigation (no existing slider in this theme implements one)
 * and prefers-reduced-motion handling (borrowed from Dawn's native
 * SlideshowComponent in global.js, since none of the testimonial sliders had it).
 *
 * Note: this is a plain .js asset (not .js.liquid), so it cannot read
 * {{ section.id }} directly. Instead, every section root carries a unique
 * id="cert-carousel-{{ section.id }}" (set in the .liquid file) and a shared
 * "certificate-recognition__carousel" class; this file discovers all
 * instances via that class.
 */
(function () {
  'use strict';

  var SWIPE_THRESHOLD = 40;

  var RESIZE_DEBOUNCE = 150;

  function CertificateRecognitionSlider(root) {
    this.root = root;
    this.track = root.querySelector('.certificate-recognition__track');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-cert-slide]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-cert-dot]'));
    this.prevBtn = root.querySelector('[data-cert-prev]');
    this.nextBtn = root.querySelector('[data-cert-next]');
    this.speed = parseInt(root.dataset.autoplaySpeed, 10) || 6000;
    this.currentIndex = 0;
    this.timer = null;
    this.resizeTimer = null;
    this.touchStartX = null;
    this.touchCurrentX = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (this.slides.length === 0) return;

    this.bindEvents();
    this.goTo(0);

    if (this.slides.length > 1) {
      this.startAutoplay();
    }
  }

  CertificateRecognitionSlider.prototype.bindEvents = function () {
    var self = this;

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        self.stopAutoplay();
        self.goTo(parseInt(dot.dataset.index, 10));
        self.startAutoplay();
      });
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.stopAutoplay();
        self.goTo(self.currentIndex - 1);
        self.startAutoplay();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.stopAutoplay();
        self.goTo(self.currentIndex + 1);
        self.startAutoplay();
      });
    }

    // Pause autoplay on hover (desktop); resume on mouseleave.
    this.root.addEventListener('mouseenter', function () {
      self.stopAutoplay();
    });
    this.root.addEventListener('mouseleave', function () {
      self.startAutoplay();
    });

    // Pause on touch, track swipe distance, resume after touchend. No
    // existing slider in this theme implements swipe navigation, so this
    // part is new — everything else here is ported from
    // ugc-testimonial-slider.js.
    this.root.addEventListener(
      'touchstart',
      function (event) {
        self.stopAutoplay();
        self.touchStartX = event.touches[0].clientX;
        self.touchCurrentX = self.touchStartX;
      },
      { passive: true }
    );

    this.root.addEventListener(
      'touchmove',
      function (event) {
        if (self.touchStartX === null) return;
        self.touchCurrentX = event.touches[0].clientX;
      },
      { passive: true }
    );

    this.root.addEventListener('touchend', function () {
      if (self.touchStartX !== null && self.touchCurrentX !== null) {
        var deltaX = self.touchCurrentX - self.touchStartX;
        if (Math.abs(deltaX) >= SWIPE_THRESHOLD) {
          if (deltaX < 0) {
            self.goTo(self.currentIndex + 1);
          } else {
            self.goTo(self.currentIndex - 1);
          }
        }
      }
      self.touchStartX = null;
      self.touchCurrentX = null;
      self.startAutoplay();
    });

    // Text reflows at different viewport widths, which changes each
    // slide's rendered height — recalculate the track height on resize,
    // debounced so it doesn't run on every intermediate resize event.
    window.addEventListener('resize', function () {
      clearTimeout(self.resizeTimer);
      self.resizeTimer = setTimeout(function () {
        self.updateTrackHeight();
      }, RESIZE_DEBOUNCE);
    });

    // Respect a live change to the OS-level reduced-motion setting.
    this.reducedMotion.addEventListener('change', function () {
      if (self.reducedMotion.matches) {
        self.stopAutoplay();
      } else {
        self.startAutoplay();
      }
    });

    // Shopify theme editor integration: jump to the block being edited
    // and pause autoplay while it's selected.
    this.slides.forEach(function (slide, index) {
      slide.addEventListener('shopify:block:select', function () {
        self.stopAutoplay();
        self.goTo(index);
      });
      slide.addEventListener('shopify:block:deselect', function () {
        self.startAutoplay();
      });
    });
  };

  CertificateRecognitionSlider.prototype.goTo = function (index) {
    var total = this.slides.length;
    var normalized = ((index % total) + total) % total;
    this.currentIndex = normalized;

    this.slides.forEach(function (slide, i) {
      var isActive = i === normalized;
      slide.classList.toggle('is-active', isActive);

      if (isActive) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    this.dots.forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === normalized);
    });

    this.updateTrackHeight();
  };

  // The track stacks every slide in the same grid cell so they can
  // crossfade (see the CSS comment above .certificate-recognition__track),
  // which left to itself auto-sizes to the TALLEST slide even while a
  // shorter one is active. Setting an explicit pixel height here — sized
  // to just the active slide — is what makes the shorter slide's empty
  // space actually collapse instead of just sitting there unused.
  CertificateRecognitionSlider.prototype.updateTrackHeight = function () {
    if (!this.track) return;
    var activeSlide = this.slides[this.currentIndex];
    if (!activeSlide) return;
    this.track.style.height = activeSlide.scrollHeight + 'px';
  };

  CertificateRecognitionSlider.prototype.startAutoplay = function () {
    if (this.slides.length <= 1 || this.timer || this.reducedMotion.matches) return;
    var self = this;
    this.timer = setInterval(function () {
      self.goTo(self.currentIndex + 1);
    }, this.speed);
  };

  CertificateRecognitionSlider.prototype.stopAutoplay = function () {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.certificate-recognition__carousel').forEach(function (root) {
      new CertificateRecognitionSlider(root);
    });
  });
})();
